import { useState } from "react";
import axios from "axios";
import { Mail, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Blocco di iscrizione alla newsletter settimanale.
 * Da integrare in NovitaNoleggioPage, HomePage, e in fondo alle pagine editoriali.
 *
 * Props:
 *  - fonte (string): tracciamento della pagina sorgente per analytics
 *  - compact (bool): versione ridotta (senza heading grande)
 */
export default function NewsletterSubscribe({ fonte = "web", compact = false }) {
  const [email, setEmail] = useState("");
  const [nome, setNome] = useState("");
  const [consenso, setConsenso] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!email || !consenso) {
      toast.error("Inserisci email e accetta l'informativa privacy");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/newsletter/subscribe`, {
        email: email.trim(),
        nome: nome.trim() || null,
        consenso_privacy: true,
        consent: true,           // alias GDPR (feb-2026 Step 5 fix)
        source_url: (typeof window !== "undefined" ? window.location.href : "").slice(0, 500),
        consent_text_version: "v1.0",
        fonte,
      });
      if (data?.status === "already_subscribed") {
        toast.success("Questa email è già iscritta. A venerdì!");
      } else {
        toast.success("Controlla la tua email per confermare l'iscrizione");
      }
      setDone(true);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Errore durante l'iscrizione";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  }

  if (done) {
    return (
      <div
        data-testid="newsletter-success"
        className={`${compact ? "p-5" : "p-8"} rounded-2xl bg-emerald-50 border border-emerald-200 text-center`}
      >
        <CheckCircle2 className="w-10 h-10 text-emerald-600 mx-auto mb-3" />
        <h3 className="text-lg font-semibold text-emerald-900 mb-1">
          Ci sei quasi!
        </h3>
        <p className="text-sm text-emerald-800 leading-relaxed max-w-md mx-auto">
          Abbiamo inviato un'email di conferma a <strong>{email}</strong>. Clicca il link per completare l'iscrizione.
          Se non la trovi, controlla la cartella spam.
        </p>
      </div>
    );
  }

  return (
    <section
      data-testid="newsletter-subscribe"
      className={`${compact ? "p-5" : "p-8 md:p-10"} rounded-2xl bg-gradient-to-br from-[#163321] to-[#0f2217] text-white relative overflow-hidden`}
    >
      {/* Pattern decorativo */}
      <div
        aria-hidden="true"
        className="absolute inset-0 opacity-[0.06] pointer-events-none"
        style={{
          backgroundImage:
            "radial-gradient(circle at 1px 1px, #fff 1px, transparent 0)",
          backgroundSize: "24px 24px",
        }}
      />

      <div className="relative z-10 grid md:grid-cols-2 gap-6 items-center">
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 backdrop-blur text-xs font-semibold tracking-wide text-[#F4C4A8] mb-3 border border-white/10">
            <Mail className="w-3.5 h-3.5" />
            Newsletter settimanale
          </div>
          {!compact && (
            <h2 className="text-2xl md:text-3xl font-bold leading-tight mb-2">
              Il digest del venerdì,<br />direttamente nella tua inbox
            </h2>
          )}
          {compact && (
            <h3 className="text-lg font-semibold mb-1">Iscriviti alla newsletter</h3>
          )}
          <p className="text-sm md:text-base text-white/75 leading-relaxed max-w-md">
            Ogni venerdì alle 18:00 ricevi le 5 notizie più importanti della settimana su
            noleggio, mobilità e novità assicurative. Zero spam, disiscrizione in 1 click.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-3" data-testid="newsletter-form" aria-labelledby="newsletter-form-heading">
          <span id="newsletter-form-heading" className="sr-only">Modulo iscrizione newsletter</span>
          <div>
            <label htmlFor="newsletter-nome-input" className="sr-only">Nome (opzionale)</label>
            <input
              id="newsletter-nome-input"
              data-testid="newsletter-nome"
              type="text"
              placeholder="Nome (opzionale)"
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-3 text-white placeholder:text-white/50 focus:outline-none focus:ring-2 focus:ring-[#F4C4A8]/40 focus:bg-white/15 transition"
              maxLength={80}
              disabled={submitting}
              autoComplete="given-name"
            />
          </div>
          <div>
            <label htmlFor="newsletter-email-input" className="sr-only">Indirizzo email</label>
            <input
              id="newsletter-email-input"
              data-testid="newsletter-email"
              type="email"
              required
              placeholder="La tua email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-3 text-white placeholder:text-white/50 focus:outline-none focus:ring-2 focus:ring-[#F4C4A8]/40 focus:bg-white/15 transition"
              maxLength={200}
              disabled={submitting}
              autoComplete="email"
              aria-required="true"
            />
          </div>
          <label htmlFor="newsletter-consenso-input" className="flex items-start gap-2.5 text-xs text-white/85 cursor-pointer leading-snug">
            <input
              id="newsletter-consenso-input"
              data-testid="newsletter-consenso"
              type="checkbox"
              checked={consenso}
              onChange={(e) => setConsenso(e.target.checked)}
              className="mt-0.5 w-4 h-4 rounded border-white/40 bg-white/10 accent-[#F4C4A8] cursor-pointer"
              required
              aria-required="true"
              disabled={submitting}
            />
            <span>
              Acconsento al trattamento dei miei dati per ricevere la newsletter informativa,
              come descritto nell&apos;
              <a
                href="/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="underline hover:text-[#F4C4A8]"
              >
                Informativa Privacy
              </a>
              . Posso revocare il consenso in qualsiasi momento.
            </span>
          </label>
          <button
            data-testid="newsletter-submit"
            type="submit"
            disabled={submitting || !email || !consenso}
            className="w-full bg-[#F4C4A8] text-[#163321] font-semibold py-3 rounded-lg hover:bg-[#f9d4bd] transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Iscrizione in corso...
              </>
            ) : (
              "Iscrivimi alla newsletter"
            )}
          </button>
        </form>
      </div>
    </section>
  );
}
